import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState, useRef } from 'react'
import { groupChatService } from '@/services/groupChatService'
import { Loader2, AlertCircle, CheckCircle, LogIn } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { useUserStore } from '@/store/userStore'

export const Route = createFileRoute('/chat/join/$inviteCode')({
    component: JoinGroupPage,
})

function JoinGroupPage() {
    const { inviteCode } = Route.useParams()
    const navigate = useNavigate()
    const [status, setStatus] = useState<'checking_auth' | 'joining' | 'success' | 'error' | 'unauthenticated'>('checking_auth')
    const [errorMessage, setErrorMessage] = useState<string>('')
    const [conversationId, setConversationId] = useState<string | null>(null)

    // Use a ref to prevent double-firing in strict mode
    const joinAttempted = useRef(false)

    const isAuthenticated = useUserStore((state) => state.isAuthenticated)
    const isLoadingUser = useUserStore((state) => state.isLoading)

    useEffect(() => {
        // Wait for user store to finish loading
        if (isLoadingUser) return

        if (!isAuthenticated) {
            setStatus('unauthenticated')
            return
        }

        // If already attempted, don't try again
        if (joinAttempted.current) return

        const joinGroup = async () => {
            if (!inviteCode) return

            joinAttempted.current = true
            setStatus('joining')

            try {
                // Attempt to join the group
                const response = await groupChatService.joinViaInvite(inviteCode)
                setConversationId(response.conversationId)
                setStatus('success')

                // Redirect to the chat after a short delay
                setTimeout(() => {
                    navigate({ to: '/chat', search: { conversationId: response.conversationId } })
                }, 1500)
            } catch (error: any) {
                // console.error('Failed to join group:', error)
                if (error.response) {
                    // console.error('Error response data:', error.response.data)
                    // console.error('Error response status:', error.response.status)
                }

                // Handle specific error cases
                if (error.response?.status === 409) {
                    // User is already a member
                    setErrorMessage('You are already a member of this group.')
                    // Try to extract conversationId if available in error response, 
                    // otherwise we might need to look it up or just redirect to chat root
                    if (error.response?.data?.conversationId) {
                        setConversationId(error.response.data.conversationId)
                    }
                } else if (error.response?.status === 404) {
                    setErrorMessage('This invite link is invalid or has expired.')
                } else {
                    setErrorMessage(
                        error.response?.data?.message ||
                        'Failed to join group. The invite link may be invalid or expired.'
                    )
                }

                setStatus('error')
            }
        }

        joinGroup()
    }, [inviteCode, navigate, isAuthenticated, isLoadingUser])

    const handleLoginRedirect = () => {
        // Redirect to login, preserving the current URL to return after login
        // Assuming /login is the route and it accepts a redirect param or handles history
        // If using TanStack Router, we might navigate differently, but window.location is safe fallback
        const currentPath = window.location.pathname
        window.location.href = `/login?redirect=${encodeURIComponent(currentPath)}`
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
            <Card className="w-full max-w-md">
                <CardHeader className="text-center">
                    <CardTitle>Joining Group</CardTitle>
                    <CardDescription>
                        {status === 'checking_auth' && 'Checking your session...'}
                        {status === 'joining' && 'Please wait while we process your invite...'}
                        {status === 'unauthenticated' && 'You need to be logged in to join this group.'}
                        {status === 'success' && 'Welcome to the group!'}
                        {status === 'error' && 'There was a problem joining the group.'}
                    </CardDescription>
                </CardHeader>
                <CardContent className="flex flex-col items-center justify-center py-6">
                    {(status === 'joining' || status === 'checking_auth') && (
                        <div className="flex flex-col items-center gap-4">
                            <Loader2 className="h-12 w-12 animate-spin text-blue-500" />
                            <p className="text-sm text-gray-500">
                                {status === 'checking_auth' ? 'Verifying session...' : 'Verifying invite code...'}
                            </p>
                        </div>
                    )}

                    {status === 'success' && (
                        <div className="flex flex-col items-center gap-4">
                            <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                                <CheckCircle className="h-8 w-8 text-green-600" />
                            </div>
                            <p className="text-lg font-medium text-green-700">Successfully joined!</p>
                            <p className="text-sm text-gray-500">Redirecting to chat...</p>
                        </div>
                    )}

                    {status === 'error' && (
                        <div className="flex flex-col items-center gap-4 text-center">
                            <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
                                <AlertCircle className="h-8 w-8 text-red-600" />
                            </div>
                            <p className="text-lg font-medium text-red-700">Unable to Join</p>
                            <p className="text-sm text-gray-500">{errorMessage}</p>
                        </div>
                    )}

                    {status === 'unauthenticated' && (
                        <div className="flex flex-col items-center gap-4 text-center">
                            <div className="h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center">
                                <LogIn className="h-8 w-8 text-amber-600" />
                            </div>
                            <p className="text-lg font-medium text-amber-700">Authentication Required</p>
                            <p className="text-sm text-gray-500">Please log in to your account to join this group chat.</p>
                        </div>
                    )}
                </CardContent>
                <CardFooter className="flex justify-center gap-2">
                    {status === 'error' && (
                        <>
                            <Button variant="outline" onClick={() => navigate({ to: '/chat' })}>
                                Go to Chat
                            </Button>
                            {/* If we have a conversation ID (e.g. already member), allow going straight there */}
                            {conversationId && (
                                <Button onClick={() => navigate({ to: '/chat', search: { conversationId } })}>
                                    Open Group
                                </Button>
                            )}
                        </>
                    )}

                    {status === 'unauthenticated' && (
                        <Button onClick={handleLoginRedirect}>
                            Log In
                        </Button>
                    )}
                </CardFooter>
            </Card>
        </div>
    )
}
